import { useEffect } from 'react';
import { Button, Checkbox, Form, Select } from 'antd';
import { UpOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

import { useSearchParams } from '../../../../hooks/useSearchParams';
import { capitalizeFirstLetter, getValidSearchParams } from '../../../../utils';
import { RangePicker } from '../../../Common/RangePicker';
import { ticketsListDefaultSearchParams } from '../../Tickets';
import { Role } from '../../../../enums/user';
import { useCategoriesGet } from '../../../../hooks/api/categories';
import { useSubCategoriesGet } from '../../../../hooks/api/subCategories';

import styles from './index.module.scss';

export interface FilterFormValues {
  role?: string;
  isShowHiddenComment?: string | boolean;
  categoryId?: string;
  subCategoryId?: string;
  updatedAt?: [Date, Date] | null;
}

interface QueryValues {
  role?: string;
  updatedAtStart?: string;
  updatedAtEnd?: string;
  isShowHiddenComment?: string;
  categoryId?: string;
  subCategoryId?: string;
}

const defaultFormValues: FilterFormValues = {
  role: '',
  isShowHiddenComment: undefined,
  categoryId: '',
  subCategoryId: '',
  updatedAt: null,
};

interface FilterProps {
  onClose: () => void;
}

export const ROLE_LIST = [
  Role.USER,
  Role.WORKER,
  Role.BUSINESS_OWNER,
];

function Filter({ onClose }: FilterProps) {
  const [form] = Form.useForm<FilterFormValues>();
  const categoriesGet = useCategoriesGet();
  const subCategoriesGet = useSubCategoriesGet();
  const { searchParams, replaceSearchParams } = useSearchParams();
  const formCategoryId = Form.useWatch('categoryId', form);

  useEffect(() => {
    if (subCategoriesGet.data && !subCategoriesGet.loading && !subCategoriesGet.error) {
      const subCategoryId = form.getFieldValue('subCategoryId');

      if (!subCategoriesGet.data.data.some(({ id }) => id === subCategoryId)) {
        form.setFieldValue('subCategoryId', undefined);
      }
    }
  }, [formCategoryId, subCategoriesGet.data]);

  const onFinish = ({
    updatedAt,
    role,
    isShowHiddenComment,
    categoryId,
    subCategoryId,
    ...values
  }: FilterFormValues) => {
    replaceSearchParams({
      ...values,
      updatedAtStart: updatedAt ? updatedAt?.[0].toISOString() : '',
      updatedAtEnd: updatedAt ? updatedAt?.[1].toISOString() : '',
      role,
      // eslint-disable-next-line no-nested-ternary
      isShowHiddenComment: isShowHiddenComment !== undefined
        ? (isShowHiddenComment || isShowHiddenComment === 'true' ? 'true' : 'false')
        : undefined,
      categoryId,
      subCategoryId,
    });
  };

  useEffect(() => {
    categoriesGet.fetch({ pageSize: 500 });
  }, []);

  useEffect(() => {
    if (formCategoryId) {
      subCategoriesGet.fetch({
        pageSize: 500,
        categoryIds: [formCategoryId as string],
      });
    }
  }, [formCategoryId]);

  useEffect(() => {
    const {
      updatedAtStart,
      updatedAtEnd,
      role,
      isShowHiddenComment,
      ...values
    } = getValidSearchParams<QueryValues>('*', searchParams);

    form.setFieldsValue({
      ...values,
      isShowHiddenComment: isShowHiddenComment !== 'false',
      updatedAt: updatedAtStart && updatedAtEnd ? [dayjs(updatedAtStart), dayjs(updatedAtEnd)] : undefined,
      role,
    });
  }, [searchParams]);

  const clear = () => {
    form.setFieldsValue({
      ...defaultFormValues,
      updatedAt: defaultFormValues.updatedAt || undefined,
    });
    replaceSearchParams(ticketsListDefaultSearchParams, { replace: true });
  };

  return (
    <Form<FilterFormValues>
      className={styles.form}
      onFinish={onFinish}
      initialValues={getValidSearchParams<FilterFormValues>('*', searchParams)}
      form={form}
    >
      <div className={styles.formRow}>
        <Form.Item label="User Type" name="role">
          <Select
            allowClear
            options={Object.values(ROLE_LIST).map((value) => ({
              label: capitalizeFirstLetter(value).replace('_', ' '),
              value,
            }))}
          />
        </Form.Item>
        <RangePicker form={form} />

        <Form.Item name="isShowHiddenComment" valuePropName="checked" label="Show hidden comments">
          <Checkbox style={{ marginRight: 5 }} />
        </Form.Item>

        <Form.Item label="Category" name="categoryId">
          <Select
            allowClear
            options={categoriesGet.data?.data.map(({ id, name }) => ({
              label: capitalizeFirstLetter(name),
              value: id,
            }))}
          />
        </Form.Item>
        <Form.Item label="Subcategory" name="subCategoryId">
          <Select
            allowClear
            loading={subCategoriesGet.loading}
            disabled={subCategoriesGet.data?.data.length === 0 || subCategoriesGet.loading}
            options={(formCategoryId ? subCategoriesGet.data?.data || [] : [])
              .map(({ id, header }) => ({
                label: capitalizeFirstLetter(header),
                value: id,
              }))}
          />
        </Form.Item>
      </div>

      <div className={styles.buttons}>
        <Button htmlType="button" onClick={clear}>
          Clear
        </Button>
        <Button htmlType="submit" type="primary">
          Apply
        </Button>
        <Button htmlType="button" onClick={onClose}>
          Close
          {' '}
          <UpOutlined />
        </Button>
      </div>
    </Form>
  );
}

export default Filter;
